import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import ErrorIcon from '@mui/icons-material/Error';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Typography,
} from '@mui/material';
import { green } from '@mui/material/colors';
import axios from 'axios';
import { useState } from 'react';
import { getAuthHeaders } from '../../utils/authUtils';

const EmailDomainMigrationTool = ({ darkMode, themeVariant }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const handleMigration = async () => {
        setIsLoading(true);
        setResult(null);
        setError(null);

        try {
            const apiBaseUrl = process.env.REACT_APP_API_URL;
            const response = await axios.post(
                `${apiBaseUrl}/api/v3/migration/email-domains`,
                {},
                { headers: getAuthHeaders(true) },
            );

            setResult(response.data);
        } catch (err) {
            console.error('Migration error:', err);
            setError(err.response?.data?.error || err.message || 'An unknown error occurred during migration');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box sx={{ textAlign: 'left' }}>
            <Paper sx={{ p: 2, mb: 3 }} elevation={1}>
                <Typography variant="body1" paragraph>
                    This tool migrates email domains from <code>@vaani.org</code> to <code>@amrutvaani.org</code> in the{' '}
                    <code>createdBy</code>, <code>updatedBy</code>, and other user-related fields across all collections
                    in the database.
                </Typography>
                <Typography variant="body1" paragraph color="warning.main">
                    <strong>Note:</strong> This operation cannot be undone. Make sure to back up your database before
                    proceeding.
                </Typography>
                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleMigration}
                        disabled={isLoading}
                        startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <EmailIcon />}
                    >
                        {isLoading ? 'Migrating...' : 'Start Migration'}
                    </Button>
                </Box>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    <Typography variant="body2">{error}</Typography>
                </Alert>
            )}

            {result && (
                <Card>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            {result.status === 'success' ? (
                                <CheckCircleIcon sx={{ color: green[500], mr: 1, fontSize: 24 }} />
                            ) : (
                                <ErrorIcon color="error" sx={{ mr: 1, fontSize: 24 }} />
                            )}
                            <Typography variant="subtitle1">
                                {result.status === 'success' ? 'Migration Completed Successfully' : 'Migration Failed'}
                            </Typography>
                        </Box>

                        {result.status === 'success' && (
                            <>
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                    Total Records Updated: <strong>{result.totalRecordsUpdated}</strong>
                                </Typography>

                                <Typography variant="subtitle2" gutterBottom>
                                    Results by Collection:
                                </Typography>

                                <List dense>
                                    {Object.entries(result.collectionResults || {}).map(([collection, count]) => (
                                        <ListItem key={collection} sx={{ py: 0.5 }}>
                                            <ListItemIcon sx={{ minWidth: 32 }}>
                                                <CheckCircleIcon fontSize="small" color="primary" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={`${collection}: ${count} record(s) updated`}
                                                primaryTypographyProps={{ variant: 'body2' }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </>
                        )}
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default EmailDomainMigrationTool;
